import React, { useMemo, useState } from 'react';
import { CloudArrowUp, X } from 'phosphor-react';
import { useDropzone } from 'react-dropzone';
import { COLORS } from '../../../../utils/colors';
import { isHTML, toInlineHTML } from '../../../../utils/helpers';

const baseStyle = {
  alignItems: 'center',
  padding: '6px 10px',
  borderWidth: 2,
  borderRadius: 8,
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border-color .24s ease-in-out, background-color .24s ease-in-out',
};

const largeStyle = {
  width: '100%',
  justifyContent: 'center',
  padding: '24px 16px',
  borderRadius: 10,
};

const acceptStyle = {
  borderColor: COLORS.SUCCESS_500,
  backgroundColor: COLORS.SUCCESS_100,
};

const rejectStyle = {
  borderColor: COLORS.ERROR_500,
  backgroundColor: COLORS.ERROR_100,
};

const errorStyle = {
  borderColor: COLORS.ERROR_500,
};

export const UploadFileInputs = ({
  label,
  labelNode,
  labelSubtext = '',
  isRequired = false,
  errorText,
  onDropProps,
  onSelectFile,
  onRemoveFile,
  selectedFile,
  selectedFileName,
  acceptedFileText,
  invalidFileMessage = 'This file type is not supported.',
  size = 'default',
}: {
  label: string;
  labelNode?: React.ReactNode;
  labelSubtext?: string;
  isRequired?: boolean;
  errorText?: string;
  onDropProps?: any;
  onSelectFile: (data: { name: string; file: File }) => void;
  onRemoveFile?: () => void;
  selectedFile?: File | null;
  selectedFileName?: string | null;
  acceptedFileText?: string;
  invalidFileMessage?: string;
  /** 'large' renders a bigger, full-width dropzone — opt in where the upload
   * is the main focus of the step, rather than one of several inline fields. */
  size?: 'default' | 'large';
}) => {
  const [fileError, setFileError] = useState('');

  const onDrop = (acceptedFiles: File[], fileRejections: any[]) => {
    setFileError('');
    if (acceptedFiles.length > 0) {
      onSelectFile({ name: label, file: acceptedFiles[0] });
      return;
    }

    const rejection = fileRejections?.[0]?.errors?.[0];
    const fileReject =
      rejection?.code === 'file-invalid-type'
        ? invalidFileMessage
        : rejection?.message || 'This file could not be uploaded.';
    setFileError(fileReject);
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    ...onDropProps,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(size === 'large' ? largeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(errorText ? errorStyle : {}),
    }),
    [size, isDragAccept, isDragReject, errorText]
  );

  return (
    <div className="w-full flex items-center">
      <div className="w-full">
        <div className="text-left">
          {labelNode ?? (
            <label className="text-xs font-semibold GREY_800-CLR">
              {isRequired && <span className="ERROR_500-CLR mr-0.5">*</span>}
              {label}
            </label>
          )}
        </div>
        {labelSubtext &&
          (isHTML(labelSubtext) ? (
            <p
              className="mb-1 text-2xs GREY_500-CLR"
              dangerouslySetInnerHTML={{ __html: toInlineHTML(labelSubtext) }}
            />
          ) : (
            <p className="mb-1 text-2xs GREY_500-CLR">{labelSubtext}</p>
          ))}
        <div
          {...getRootProps({
            style,
            'aria-label': `${label}. Click or drag a file to upload`,
          })}
          className={`group cursor-pointer flex items-center WHITE-BG border-[#CBD5E1] focus:outline-none focus:border-[#3762FB] focus:bg-[#F9FAFF] ${
            size === 'large' ? 'flex-col text-center' : ''
          }`}>
          <CloudArrowUp
            size={size === 'large' ? 32 : 24}
            color={isDragReject ? COLORS.ERROR_500 : COLORS.PRIMARY_500}
            aria-hidden="true"
          />
          <div className={size === 'large' ? 'mt-2 max-w-full' : 'ml-2 min-w-0'}>
            <p
              className={`truncate font-medium GREY_800-CLR ${
                size === 'large' ? 'text-sm' : 'text-xs'
              }`}>
              {selectedFile?.name || selectedFileName || 'Click or drag and drop your file here'}
            </p>
            {acceptedFileText && !selectedFile && !selectedFileName && (
              <p className="mt-1 text-xs GREY_500-CLR">{acceptedFileText}</p>
            )}
          </div>
          <input
            {...getInputProps()}
            aria-describedby={errorText ? `${label}-upload-error` : undefined}
          />
          {(selectedFile || selectedFileName) && onRemoveFile && (
            <button
              type="button"
              aria-label={`Remove ${selectedFile?.name || selectedFileName || label}`}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFile();
              }}
              className="ml-2 inline-flex min-h-6 min-w-6 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-red-50 hover:text-red-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-1">
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
        {errorText && (
          <p id={`${label}-upload-error`} role="alert" className="mt-1 text-xs ERROR_600-CLR">
            {errorText}
          </p>
        )}
        {fileError && (
          <p role="alert" className="mt-1 text-xs ERROR_600-CLR">
            {fileError}
          </p>
        )}
      </div>
    </div>
  );
};
